<template>
    <div class="dispatch-email">
        <h2 class="content-block">Dispatch &#10095; Email</h2>

        <div class="content-block dx-card responsive-paddings">
            <div class="field-row">
                <label class="field-label">To</label>
                <DxTextBox v-model:value="to" placeholder="recipient@example.com" mode="email" />
            </div>
            <div class="field-row">
                <label class="field-label">Subject</label>
                <DxTextBox v-model:value="subject" />
            </div>

            <label class="field-label editor-label">Message</label>
            <DxHtmlEditor v-model:value="emailHtml" height="500px">
                <DxMediaResizing :enabled="true" />
                <DxImageUpload :enabled="false" />
                <DxToolbar :multiline="true">
                    <DxItem name="undo" />
                    <DxItem name="redo" />
                    <DxItem name="separator" />
                    <DxItem :accepted-values="['Arial', 'Georgia', 'Tahoma', 'Times New Roman', 'Verdana']"
                        name="font" />
                    <DxItem :accepted-values="['8pt', '10pt', '12pt', '14pt', '18pt', '24pt']" name="size" />
                    <DxItem name="separator" />
                    <DxItem name="bold" />
                    <DxItem name="italic" />
                    <DxItem name="underline" />
                    <DxItem name="strike" />
                    <DxItem name="separator" />
                    <DxItem name="color" />
                    <DxItem name="background" />
                    <DxItem name="separator" />
                    <DxItem :accepted-values="[1, 2, 3, false]" name="header" />
                    <DxItem name="separator" />
                    <DxItem name="alignLeft" />
                    <DxItem name="alignCenter" />
                    <DxItem name="alignRight" />
                    <DxItem name="separator" />
                    <DxItem name="orderedList" />
                    <DxItem name="bulletList" />
                    <DxItem name="separator" />
                    <DxItem name="link" />
                    <DxItem name="clear" />
                </DxToolbar>
            </DxHtmlEditor>

            <div v-if="sent" class="status success">
                Email sent to {{ to || 'recipient' }}.
            </div>
            <div v-else-if="error" class="status error">{{ error }}</div>

            <div class="actions">
                <DxButton text="Cancel" styling-mode="outlined" @click="goBack" />
                <DxButton text="Send" type="default" icon="email" :disabled="sending || sent"
                    @click="send" />
            </div>
        </div>
    </div>
</template>

<script>
import DxTextBox from 'devextreme-vue/text-box';
import { DxButton } from 'devextreme-vue/button';
import {
    DxHtmlEditor,
    DxToolbar,
    DxItem,
    DxMediaResizing,
    DxImageUpload,
} from 'devextreme-vue/html-editor';
const ULLAGE_UNIT = 'L';
// Public, unauthenticated URL so recipients' mail clients can load the logo.
const LOGO_URL = 'https://r3sharedprd.blob.core.windows.net/assets/r3_logo_sm.png';

export default {
    name: 'DispatchEmail',
    components: {
        DxTextBox,
        DxButton,
        DxHtmlEditor,
        DxToolbar,
        DxItem,
        DxMediaResizing,
        DxImageUpload,
    },
    data() {
        return {
            to: '',
            subject: '',
            emailHtml: '',
            sending: false,
            sent: false,
            error: null,
        };
    },
    computed: {
        dispatch() {
            return this.$store.getters['dispatch/getCurrentDispatch'];
        },
        params() {
            return this.dispatch.parameters;
        },
        sites() {
            return this.dispatch.selectedSites || [];
        },
        totalFill() {
            return this.sites.reduce((sum, s) => sum + (Number(s.fillAmount) || 0), 0);
        },
        // Re-derived against the current dispatch date rather than read off the
        // saved rows, so a date change is reflected in the email.
        forecastSites() {
            return this.$store.getters['dispatch/getCurrentDispatchSitesWithCoords'];
        },
        totalPredictedFill() {
            return this.forecastSites.reduce(
                (sum, s) => sum + (Number(s.predictedFillAmount) || 0),
                0
            );
        },
        forecastDays() {
            return this.$store.getters['dispatch/getDispatchForecastDays'];
        },
    },
    created() {
        // Rehydrate the working dispatch if the store started empty (e.g. a
        // fresh tab / refresh), then build the template.
        this.$store.dispatch('dispatch/restoreCachedDispatch');
        this.subject = `Dispatch: ${this.dispatch.name || 'Untitled'}`;
        this.emailHtml = this.buildTemplate();
    },
    methods: {
        formatUllage(value) {
            return Math.round(Number(value) || 0).toLocaleString();
        },
        fmtMulti(value) {
            if (Array.isArray(value)) return value.length ? value.join(', ') : 'All';
            return value || 'All';
        },
        fmtDate(value) {
            if (!value) return '&mdash;';
            const d = new Date(value);
            return isNaN(d) ? value : d.toLocaleDateString();
        },
        logoUrl() {
            return LOGO_URL;
        },
        mapUrl() {
            const base = window.location.href.split('#')[0];
            const query = this.dispatch.dispatchId ? { id: this.dispatch.dispatchId } : {};
            const href = this.$router.resolve({ name: 'dispatch-map', query }).href;
            return base + href;
        },
        buildTemplate() {
            const p = this.params;
            const name = this.dispatch.name || '(unnamed dispatch)';
            const row = (label, value) =>
                `<tr>
                    <td style="padding:4px 14px 4px 0; color:#667; font-size:13px;">${label}</td>
                    <td style="padding:4px 0; font-size:13px; font-weight:600;">${value}</td>
                </tr>`;

            return `
<div style="font-family: Arial, Helvetica, sans-serif; color:#222; max-width:640px;">
    <div>
    <img src="${this.logoUrl()}" alt="R3mote" style="height:46px; margin-bottom:18px;" />
    </div>
    <h2 style="color:#1e2d3d; margin:0 0 4px;">Dispatch: ${name}</h2>
    <p style="font-size:14px; line-height:1.5;">Hello,</p>
    <p style="font-size:14px; line-height:1.5;">
        A chemical dispatch has been prepared. The details and delivery summary are below.
    </p>

    <h3 style="color:#1e2d3d; margin:18px 0 6px;">Dispatch Notes</h3>
    <table style="border-collapse:collapse;">
        ${row('Dispatch Date', this.fmtDate(p.dispatchDate))}
        ${row('Product', this.fmtMulti(p.product))}
        ${row('Operator', this.fmtMulti(p.operator))}
        ${row('Area', this.fmtMulti(p.area))}
        ${row('Maximum DTE', p.maxDte != null ? p.maxDte + ' days' : '&mdash;')}
        ${row('Sites', this.sites.length)}
        ${row('Total Fill Amount', this.formatUllage(this.totalFill) + ' ' + ULLAGE_UNIT)}
        ${row('Predicted Fill Amount', this.formatUllage(this.totalPredictedFill) + ' ' + ULLAGE_UNIT)}
    </table>

    <p style="font-size:14px; line-height:1.5; margin-top:18px;">
        <strong>Total chemical to deliver: ${this.formatUllage(this.totalPredictedFill)} ${ULLAGE_UNIT}</strong>
        across ${this.sites.length} site${this.sites.length === 1 ? '' : 's'}.
    </p>
    <p style="font-size:12px; line-height:1.5; color:#667;">
        Predicted Fill Amount is each site's requirement on the dispatch
        date${this.forecastDays ? ` (${this.forecastDays} day${this.forecastDays === 1 ? '' : 's'} out)` : ''},
        projected from its current volume and Injection Avg. Requirement today is
        ${this.formatUllage(this.totalFill)} ${ULLAGE_UNIT}.
    </p>

    <p style="margin:22px 0;">
        <a href="${this.mapUrl()}"
           style="background:#e8a838; color:#1e2d3d; text-decoration:none; font-weight:600;
                  padding:11px 20px; border-radius:6px; display:inline-block; font-size:14px;">
            View Dispatch Map
        </a>
    </p>
    <p style="font-size:12px; color:#889;">
        Or paste this link into your browser:<br/>
        <a href="${this.mapUrl()}" style="color:#3a7bd5;">${this.mapUrl()}</a>
    </p>

    <p style="font-size:14px; line-height:1.5; margin-top:20px;">
        Thank you,<br/>
        <strong>R3mote Dispatch</strong>
    </p>
</div>`.trim();
        },
        async send() {
            if (!this.to || !/^\S+@\S+\.\S+$/.test(this.to)) {
                this.error = 'Enter a valid recipient email address.';
                return;
            }
            this.error = null;
            this.sending = true;
            try {
                await this.$store.dispatch('dispatch/sendDispatchEmail', {
                    dispatchId: this.dispatch.dispatchId,
                    to: this.to,
                    subject: this.subject,
                    html: this.emailHtml,
                });
                this.sent = true;
            } catch (e) {
                this.error = e.message || 'Failed to send email.';
            } finally {
                this.sending = false;
            }
        },
        goBack() {
            this.$router.back();
        },
    },
};
</script>

<style scoped>
.dispatch-email h2 {
    margin-left: 0.5em;
}

.field-row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
}

.field-label {
    width: 70px;
    font-size: 14px;
    font-weight: lighter;
    flex-shrink: 0;
}

.field-row .dx-textbox {
    flex: 1;
}

.editor-label {
    display: block;
    width: auto;
    margin-bottom: 8px;
}

.status {
    margin-top: 16px;
    font-size: 13px;
}

.status.success {
    color: #4caf50;
}

.status.error {
    color: #d9534f;
}

.actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
}

/* Light grey background for the rich-text editor content area. */
:deep(.dx-htmleditor-content) {
    background-color: #f0f0f0;
}
</style>
